import { Box, Grid, IconButton, Stack, Typography } from "@mui/material";
import CustomButton from "components/atoms/CustomButton";
import RichTextEditor from "components/atoms/richTextEditor/RichTextEditor";
import VoiceNotePlayBack from "components/atoms/voiceNotePlayback";
import VoiceRecorder from "components/atoms/VoiceRecorder";
import { useFormik } from "formik";
import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { GET_PATIENT_RECENT_VISIT } from "utils/reactQueryKeys";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import secureLocalStorage from "react-secure-storage";
import { debounce } from "utils/debounce";

const initialValues = {
  history: "",
  examination: "",
  findings: "",
  diagnosis: "",
  plan: "",
};

const RecordVoiceNote = ({ setAudioObjArr }) => {
  return (
    <Stack direction={"column"} gap={3}>
      <Typography sx={{ fontWeight: "700", lineHeight: "18px", color: "primary.formLabel", fontSize: "16px" }}>
        Record voice note
      </Typography>
      <VoiceRecorder setAudioObjArr={setAudioObjArr} />
    </Stack>
  );
};

function DoctorsNote({ handleNext }) {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [audioObjArr, setAudioObjArr] = useState([]);
  const [attachments, setAttachments] = useState({
    history: [],
    examination: [],
    findings: [],
    diagnosis: [],
    plan: [],
  });

  const savedValues = secureLocalStorage.getItem(`hms_patient_${id}`) || null;

  const { handleChange, values } = useFormik({
    initialValues: savedValues || initialValues,
  });

  const { data: recentVisit } = useCustomQuery(
    [GET_PATIENT_RECENT_VISIT, id],
    { url: `/visit/recent/${id}`, method: "get" },
    { refetchOnWindowFocus: false }
  );

  // MUTATION 1: To save the final note to the database
  const { mutateAsync: saveNoteMutation, isLoading: isSaving } = useCustomMutation(
    { url: `/visit/${recentVisit?.data?._id}`, method: "patch" },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_PATIENT_RECENT_VISIT, id]);
        toast.success("Success");
        secureLocalStorage.removeItem(`hms_patient_${id}`);
        handleNext();
      },
      onError: (error) => {
        const messages = Array.isArray(error.message) ? error.message : [error.message];
        messages.forEach(msg => toast.error(msg));
      },
    }
  );

  // MUTATION 2: The right way to hit our new backend upload endpoint
  const { mutateAsync: uploadFilesMutation, isLoading: isUploading } = useCustomMutation(
    { url: `/visit/upload-attachments`, method: "post" }
  );

  const handleFileChange = (section, e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => ({ ...prev, [section]: [...prev[section], ...files] }));
  };

  const onDeleteVN = (audioUrl) => {
    setAudioObjArr((prev) => prev.filter((_audio) => _audio.audioUrl !== audioUrl));
  };

  const handleAdd = async () => {
    // Helper function to build FormData and call our new backend mutation
    const uploadToBackend = async (filesOrBlobs, isVoiceNote = false) => {
      if (!filesOrBlobs || filesOrBlobs.length === 0) return [];
      
      const formData = new FormData();
      
      filesOrBlobs.forEach((item, index) => {
        if (isVoiceNote) {
          // Blobs need a filename appended to work nicely with Multer
          formData.append('files', item.audioBlob, `voicenote_${index}.webm`);
        } else {
          formData.append('files', item);
        }
      });

      try {
        // Send FormData to our NestJS endpoint
        const res = await uploadFilesMutation(formData);
        // Depending on your Axios setup, the response data is usually in res.data or directly in res
        return res?.data?.urls || res?.urls || [];
      } catch (error) {
        toast.error("Failed to upload attachments.");
        throw error;
      }
    };

    // Upload all sections + voice notes to our backend in parallel
    try {
      const [hFiles, eFiles, fFiles, dFiles, pFiles, vns] = await Promise.all([
        uploadToBackend(attachments.history),
        uploadToBackend(attachments.examination),
        uploadToBackend(attachments.findings),
        uploadToBackend(attachments.diagnosis),
        uploadToBackend(attachments.plan),
        uploadToBackend(audioObjArr, true), // Flagged as voice notes for Blob handling
      ]);

      // Construct final payload with secure URLs
      const payload = {
        assessmentLog: [
          {
            note: {
              ...values,
              historyFiles: hFiles,
              examinationFiles: eFiles,
              findingsFiles: fFiles,
              diagnosisFiles: dFiles,
              planFiles: pFiles,
            },
            voiceNotes: vns,
          },
        ],
      };

      // Patch the visit
      await saveNoteMutation(payload);
      
    } catch (err) {
      console.error("Upload process interrupted", err);
    }
  };

  const debouncedSave = debounce((e) => {
    const data = { ...values, [e.target.name]: e.target.value };
    secureLocalStorage.setItem(`hms_patient_${id}`, data);
  }, 1000);

  const onChange = (e) => {
    handleChange(e);
    debouncedSave(e);
  };

  const isFormDirty = 
    values.history?.trim() || values.examination?.trim() || values.findings?.trim() || 
    values.diagnosis?.trim() || values.plan?.trim() || audioObjArr.length > 0 ||
    Object.values(attachments).some((arr) => arr.length > 0);

  return (
    <Box>
      <Stack sx={{ width: { xs: "100%", sm: "90%", lg: "68%" }, mt: 2 }} direction={"column"} gap={4}>
        
        {['history', 'examination', 'findings', 'diagnosis', 'plan'].map((field) => (
          <Box key={field}>
            <RichTextEditor
              handleChange={onChange}
              value={values[field]}
              name={field}
              title={field.charAt(0).toUpperCase() + field.slice(1)}
              placeholder="Start typing here"
            />
            
            <Stack direction="row" spacing={1} alignItems="center" mt={1}>
              <IconButton component="label" size="small" color="primary">
                <input hidden type="file" multiple onChange={(e) => handleFileChange(field, e)} />
                <AttachFileIcon />
              </IconButton>
              <Typography variant="caption" color="text.secondary">
                {attachments[field].length > 0 ? `${attachments[field].length} file(s) attached` : "Attach images or scans"}
              </Typography>
            </Stack>
          </Box>
        ))}

        <RecordVoiceNote setAudioObjArr={setAudioObjArr} />

        <Grid container rowSpacing={2} columnSpacing={5} sx={{ mt: 1 }}>
          {audioObjArr.map((audio) => (
            <Grid item xs={12} sm={6} key={audio.audioUrl}>
              <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                <VoiceNotePlayBack audioUrl={audio.audioUrl} />
                <IconButton size="small" onClick={() => onDeleteVN(audio.audioUrl)}>
                  <DeleteIcon style={{ color: "#E64034" }} />
                </IconButton>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Stack>

      <Stack direction={"row"} justifyContent="space-between" mt={3}>
        <CustomButton
          text={isUploading ? "Uploading..." : "Save Tab"}
          sx={{ minWidth: "30%" }}
          disabled={isSaving || isUploading || !isFormDirty}
          onClick={handleAdd}
          color="secondary"
        />
        <CustomButton text={"Skip"} onClick={handleNext} variant="containedBrown" sx={{ minWidth: "30%" }} />
      </Stack>
    </Box>
  );
}

export default DoctorsNote;